import React from 'react'
import CommonAttachmentModal from '../../../components/commonAttachmentModal/commonAttachmentModal'
import Spinner from '../../../components/common/spinner/spinner'

/**
 * RfpAttachmentModal Component
 * 
 * A specialized attachment modal for RFP documents with user permissions
 * and status-based restrictions. Supports multiple file uploads.
 * 
 * @param {Object} props
 * @param {boolean} props.visible - Controls modal visibility
 * @param {Function} props.onClose - Handler for closing the modal
 * @param {Object} props.rfp - Current RFP record with attachments
 * @param {Array} props.fileList - List of files being uploaded
 * @param {Function} props.setFileList - Function to update file list
 * @param {Object} props.actionLoading - Loading states for various actions
 * @param {Object} props.currentUser - Current logged in user information
 * @param {Function} props.handleUploadFile - Handler for file upload (supports single or multiple files)
 * @param {Function} props.handlePreviewDocument - Handler for document preview
 * @param {Function} props.handleDownloadDocument - Handler for document download
 * @param {Function} props.handleDeleteDocument - Handler for document deletion
 */
const RfpAttachmentModal = ({
  visible,
  onClose,
  rfp,
  fileList,
  setFileList,
  actionLoading,
  currentUser,
  handleUploadFile,
  handlePreviewDocument,
  handleDownloadDocument,
  handleDeleteDocument
}) => {
  // Check if the current user created this RFP
  const isCreatedByCurrentUser = React.useMemo(() => {
    if (!currentUser || !rfp) return false

    const emailMatch = rfp.createdByEmail?.toLowerCase() === currentUser.username?.toLowerCase()
    const nameMatch = rfp.createdBy?.toLowerCase() === currentUser.name?.toLowerCase()

    return emailMatch || nameMatch
  }, [currentUser, rfp])

  // Check if RFP is in a state that allows modifications
  const isRfpModifiable = React.useMemo(() => {
    if (!rfp) return false

    // Allow modifications only for Draft and Pending statuses
    const modifiableStatuses = ['Draft', 'Pending', 'DRAFT', 'PENDING']
    return modifiableStatuses.includes(rfp.status)
  }, [rfp])

  // Determine if modifications should be disabled
  const disableModifications = !isRfpModifiable || !isCreatedByCurrentUser

  // Generate appropriate tooltip message
  const disabledTooltipMessage = React.useMemo(() => {
    if (!isRfpModifiable) {
      return `Cannot modify attachments for RFPs with status: ${rfp?.status}`
    }
    if (!isCreatedByCurrentUser) {
      return 'You can only modify attachments for RFPs you created'
    }
    return ''
  }, [isRfpModifiable, isCreatedByCurrentUser, rfp])

  // Configuration for the attachment modal
  const config = {
    enableUpload: true,
    enableDownload: true,
    enablePreview: true,
    enableDelete: true,
    disableModifications,
    disabledTooltipMessage,
    showOriginalFilename: true,
    maxFileSize: 10, // 10MB
    acceptedTypes: ['image/*', '.pdf', 'application/pdf', '.doc', '.docx', '.xls', '.xlsx'],
    allowedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/gif',
      'image/bmp',
      'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ],
    uploadText: 'Click or drag files to this area to upload',
    uploadHint: 'Support for single or bulk upload. PDF, Images, Word, and Excel files are allowed. Multiple files will be uploaded simultaneously.',
    multiple: true,
    requireAtLeastOneAttachment: true
  }

  // Sanitize file names by replacing spaces with underscores
  const sanitizeFileName = React.useCallback((file) => {
    if (!file?.name) return file

    const newName = file.name.replace(/\s+/g, '_')
    if (newName === file.name) return file

    const sanitizedFile = new File([file], newName, { type: file.type })

    // Copy over any additional properties from the original file
    Object.keys(file).forEach(key => {
      if (key !== 'name') {
        try {
          sanitizedFile[key] = file[key]
        } catch {
          // Some properties might be read-only
        }
      }
    })

    return sanitizedFile
  }, [])

  // Memoized handlers
  const handlers = React.useMemo(() => ({
    handleUploadFile: (files) => {
      const fileArray = Array.isArray(files) ? files : [files]
      const sanitizedFiles = fileArray.map(file => sanitizeFileName(file))

      return handleUploadFile(sanitizedFiles.length === 1 ? sanitizedFiles[0] : sanitizedFiles)
    },

    handlePreviewDocument: (doc) => {
      handlePreviewDocument(doc)
    },

    handleDownloadDocument: (doc) => {
      handleDownloadDocument(doc)
    },

    handleDeleteDocument: (docId, attachmentId) => {
      handleDeleteDocument(docId, attachmentId)
    },

    onAttachmentsUpdate: () => {
      // Handle any additional logic needed for RFP attachments
      // This could trigger a refresh of the RFP data if needed
    }
  }), [sanitizeFileName, handleUploadFile, handlePreviewDocument, handleDownloadDocument, handleDeleteDocument])

  // Memoized loading states
  const loadingStates = React.useMemo(() => ({
    uploadAttachment: actionLoading?.uploadAttachment || false,
    previewAttachment: actionLoading?.previewAttachment || {},
    downloadAttachment: actionLoading?.downloadAttachment || {},
    deleteAttachment: actionLoading?.deleteAttachment || {}
  }), [actionLoading])

  // Custom spinner component for upload loading state
  const CustomSpinner = React.useCallback(() => (
    <div className="upload-loading">
      <Spinner size="small" />
      <p>Uploading files...</p>
    </div>
  ), [])

  if (!rfp) {
    return null
  }

  return (
    <CommonAttachmentModal
      visible={visible}
      onClose={onClose}
      title={`Attachments for RFP #${rfp?.transactionId || rfp?.rfpId || 'N/A'}`}
      existingAttachments={rfp?.attachments || []}
      fileList={fileList}
      setFileList={setFileList}
      config={config}
      handlers={handlers}
      loadingStates={loadingStates}
      customSpinner={CustomSpinner}
    />
  )
}

export default RfpAttachmentModal